import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getConsent, setConsent, CONSENT, CONSENT_CHANGED_EVENT } from '../../helpers/consent';
import { loadAnalytics, disableAnalytics } from '../../helpers/analytics';

const Banner = styled.aside`
  position: fixed;
  left: 25px;
  right: 25px;
  bottom: 108px;
  z-index: 200;
  background-color: #fff;
  color: #045F61;
  border-radius: 8px;
  box-shadow: 0 0 8px 0 rgb(4 95 97 / 19%);
  padding: 18px 22px;
  font-family: 'Roboto', sans-serif;
  box-sizing: border-box;
  @media (min-width: 900px) {
    left: auto;
    right: 24px;
    bottom: 24px;
    width: 420px;
  }
  h4 {
    margin: 0 0 8px 0;
    font-size: 17px;
    font-weight: 500;
  }
  p {
    margin: 0 0 16px 0;
    font-size: 14px;
    line-height: 1.5;
  }
  a {
    color: inherit;
    font-weight: 500;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    &:hover { color: #E49806; }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

const ChoiceButton = styled.button`
  flex: 1;
  height: 42px;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #045F61;
  background-color: ${props => props.primary ? '#045F61' : '#fff'};
  color: ${props => props.primary ? '#fff' : '#045F61'};
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  &:hover,
  &:focus-visible {
    background-color: ${props => props.primary ? '#367E80' : '#E5F8F4'};
    border-color: ${props => props.primary ? '#367E80' : '#045F61'};
    outline: none;
  }
`;

const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => getConsent() === null);

  useEffect(() => {
    if (getConsent() === CONSENT.ACCEPTED) {
      loadAnalytics();
    }
    const onChange = (e) => setVisible(e.detail === null);
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
  }, []);

  const accept = () => {
    setConsent(CONSENT.ACCEPTED);
    loadAnalytics();
  };

  const refuse = () => {
    setConsent(CONSENT.REFUSED);
    disableAnalytics();
  };

  if (!visible) return null;

  return (
    <Banner role="dialog" aria-live="polite" aria-labelledby="cookie-banner-title">
      <h4 id="cookie-banner-title">{t('cookieTitle')}</h4>
      <p>
        {t('cookieText')}{' '}
        <Link to="/settings">{t('cookieMore')}</Link>
      </p>
      <Actions>
        <ChoiceButton type="button" onClick={refuse}>{t('cookieRefuse')}</ChoiceButton>
        <ChoiceButton type="button" primary onClick={accept}>{t('cookieAccept')}</ChoiceButton>
      </Actions>
    </Banner>
  );
};

export default CookieBanner;
